import type { ITransferDeposit } from '@interfaces/Transfer';
import { deepFindTransferId } from './deepTransferId';

/** Chuẩn hóa param giống transferSlice (deposit thunk). */
export function normalizeDepositParam(param: ITransferDeposit): ITransferDeposit {
  const p: Record<string, unknown> = { ...param };
  delete p.money;
  const imgs = p.images as unknown[] | undefined;
  if (!imgs || !Array.isArray(imgs) || imgs.length === 0) {
    delete p.images;
  }
  return p as ITransferDeposit;
}

/** Chuỗi số / number → id dương; tránh NaN và 0. */
export function coercePositiveIntId(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  if (typeof v === 'string' && v.trim() === '') return null;
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.floor(n);
}

function assignDefined(target: Record<string, unknown>, source: Record<string, unknown>): void {
  for (const [k, v] of Object.entries(source)) {
    if (v !== undefined) {
      target[k] = v;
    }
  }
}

/**
 * Gộp body JSON từ create-transfer-money (root + data + deep transfer_id).
 * Không dùng `{...a,...b}` thuần — tránh `data` ghi đè `transfer_id` ở root bằng undefined.
 */
export function buildDepositMetaFromApiRes(res: unknown): Record<string, unknown> {
  let r: any = res;
  if (typeof r === 'string') {
    try {
      r = JSON.parse(r);
    } catch {
      return {};
    }
  }
  if (r === null || typeof r !== 'object' || Array.isArray(r)) {
    return {};
  }
  let rawInner: unknown = r?.data;
  if (typeof rawInner === 'string') {
    try {
      rawInner = JSON.parse(rawInner);
    } catch {
      rawInner = undefined;
    }
  }
  const inner: Record<string, unknown> =
    rawInner !== null &&
    rawInner !== undefined &&
    typeof rawInner === 'object' &&
    !Array.isArray(rawInner)
      ? (rawInner as Record<string, unknown>)
      : {};

  const root = { ...(r as Record<string, unknown>) };
  assignDefined(root, inner);

  const meta = root;
  const tidFromRoot = coercePositiveIntId((r as Record<string, unknown>).transfer_id);
  const tidFromInner = coercePositiveIntId(inner.transfer_id ?? inner.transferId);
  const tidFromId = coercePositiveIntId(meta.id ?? inner.id);
  const deepTid = deepFindTransferId(r);
  const chosen =
    tidFromRoot ?? tidFromInner ?? tidFromId ?? deepTid ?? coercePositiveIntId(meta.transfer_id);
  if (chosen) {
    meta.transfer_id = chosen;
  }

  const bill =
    (typeof meta.bill_no === 'string' && meta.bill_no.trim() ? meta.bill_no : null) ??
    (typeof inner.bill_no === 'string' && inner.bill_no.trim() ? inner.bill_no : null) ??
    (typeof (r as any).bill_no === 'string' && (r as any).bill_no.trim() ? (r as any).bill_no : null);
  if (bill) {
    meta.bill_no = bill;
  }

  return meta;
}

/**
 * Lấy transfer_id từ response create-transfer-money (mọi biến thể bọc data / id).
 */
export function extractDepositTransferIdFromCreateResponse(res: unknown): number | null {
  const meta = buildDepositMetaFromApiRes(res);
  const candidates = [
    meta.transfer_id,
    meta.transferId,
    (meta as Record<string, unknown>).transferID,
    meta.id,
    (meta as any).data?.transfer_id,
    (meta as any).data?.id,
    (meta as any).result?.transfer_id,
    (meta as any).payload?.transfer_id,
  ];
  for (const c of candidates) {
    const id = coercePositiveIntId(c);
    if (id) return id;
  }
  const deep = deepFindTransferId(res);
  if (deep) return deep;
  if (res && typeof res === 'object' && !Array.isArray(res)) {
    const o = res as Record<string, unknown>;
    const data = o.data;
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      const d = data as Record<string, unknown>;
      const t = coercePositiveIntId(d.transfer_id ?? d.transferId ?? d.id);
      if (t) return t;
    }
  }
  return null;
}
